import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Input, Label, Modal, Table, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import { DatePickerField } from '@/components/datePickerField';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import {
    isoToDisplayDate,
    NIL_AMOUNT,
} from '@/pages/accounting/statements/statementMeta';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    CALCULATOR_DIRECTIONS,
    directionFieldLabelKey,
} from '../../dividendsMeta';
import { useDeclareDistribution } from './useDeclareDistribution';

type DeclareDialogProps = {
    isOpen: boolean;
    locale: LocaleCode;
    onClose: () => void;
};

/**
 * Declare a distribution.
 *
 * Pick an amount and a date, see the per-shareholder split with column totals,
 * then build **one** balanced draft entry and hand it to the Journal Entry form.
 * Nothing posts automatically.
 *
 * The parts that carry weight:
 *
 * - **The gate is stated, not just enforced.** When the distribution plus its CIT
 *   exceeds distributable profit, the button is disabled *and* the shortfall is
 *   shown — because "greyed out" without a figure is a dead end.
 * - **Allocation is by holding, with per-shareholder override.** An overridden row
 *   is marked, and the remainder is deliberately not redistributed under the other
 *   holders.
 * - **The due date** — the 15th of the following month, when both the CIT and the
 *   withheld tax fall due — is shown as information. Nothing here files.
 */
export const DeclareDialog: React.FC<DeclareDialogProps> = ({
    isOpen,
    locale,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const model = useDeclareDistribution({ locale, onClose });

    return (
        <>
            <Modal
                isOpen={isOpen && model.draft === null}
                onOpenChange={(open) => !open && onClose()}
            >
                <Modal.Backdrop>
                    <Modal.Container size="lg">
                        <Modal.Dialog
                            className="w-full max-w-4xl"
                            {...getNodeTestIdProp('dividends-declare-dialog')}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    {t('dividends.declare.title')}
                                </Modal.Heading>
                                <p className="text-sm text-muted">
                                    {t('dividends.declare.subtitle')}
                                </p>
                            </Modal.Header>

                            <Modal.Body className="flex flex-col gap-4 pb-2">
                                <div className="flex flex-wrap items-end gap-4">
                                    <Segment
                                        aria-label={t(
                                            'dividends.calculator.directionAria',
                                        )}
                                        onSelectionChange={(key) =>
                                            model.onDirectionChange(
                                                key as never,
                                            )
                                        }
                                        selectedKey={model.direction}
                                        size="sm"
                                        {...getNodeTestIdProp(
                                            'dividends-declare-direction',
                                        )}
                                    >
                                        {CALCULATOR_DIRECTIONS.map((option) => (
                                            <Segment.Item
                                                id={option.id}
                                                key={option.id}
                                            >
                                                <Segment.Separator />
                                                {t(option.labelKey)}
                                            </Segment.Item>
                                        ))}
                                    </Segment>

                                    <TextField
                                        className="flex w-full max-w-[14rem] flex-col gap-1"
                                        onChange={model.onAmountChange}
                                        value={model.amount}
                                        {...getNodeTestIdProp(
                                            'dividends-declare-amount',
                                        )}
                                    >
                                        <Label>
                                            {t(
                                                directionFieldLabelKey(
                                                    model.direction,
                                                ),
                                            )}
                                        </Label>
                                        <Input
                                            className="text-right tabular-nums"
                                            inputMode="decimal"
                                            placeholder="0.00"
                                        />
                                    </TextField>

                                    <div className="w-full max-w-[14rem]">
                                        <DatePickerField
                                            id="dividends-declare-date"
                                            label={t(
                                                'dividends.declare.dateLabel',
                                            )}
                                            name="date"
                                            onChange={model.onDateChange}
                                            value={model.date}
                                        />
                                    </div>

                                    <Button
                                        isPending={model.isPreviewing}
                                        onPress={model.onPreview}
                                        variant="outline"
                                        {...getNodeTestIdProp(
                                            'dividends-declare-preview',
                                        )}
                                    >
                                        {t('dividends.declare.preview')}
                                    </Button>
                                </div>

                                {model.error ? (
                                    <p
                                        className="text-sm text-danger"
                                        {...getNodeTestIdProp(
                                            'dividends-declare-error',
                                        )}
                                    >
                                        {model.error}
                                    </p>
                                ) : null}

                                {model.hasPreview && model.figures ? (
                                    <>
                                        <div
                                            {...getNodeTestIdProp(
                                                'dividends-declare-table',
                                            )}
                                        >
                                            <Table>
                                                <Table.ScrollContainer className="max-h-72 overflow-auto">
                                                    <Table.Content
                                                        aria-label={t(
                                                            'dividends.declare.tableAria',
                                                        )}
                                                        className="min-w-[760px] text-sm"
                                                    >
                                                        <Table.Header>
                                                            <Table.Column
                                                                id="name"
                                                                isRowHeader
                                                            >
                                                                {t(
                                                                    'dividends.columns.name',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="holding">
                                                                {t(
                                                                    'dividends.columns.holding',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="gross">
                                                                {t(
                                                                    'dividends.declare.columns.gross',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="withholding">
                                                                {t(
                                                                    'dividends.declare.columns.withholding',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="net">
                                                                {t(
                                                                    'dividends.declare.columns.net',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="cit">
                                                                {t(
                                                                    'dividends.declare.columns.cit',
                                                                )}
                                                            </Table.Column>
                                                        </Table.Header>
                                                        <Table.Body>
                                                            {model.rows.map(
                                                                (row) => (
                                                                    <Table.Row
                                                                        id={
                                                                            row.shareholderId
                                                                        }
                                                                        key={
                                                                            row.shareholderId
                                                                        }
                                                                        {...getNodeTestIdProp(
                                                                            `dividends-declare-row-${row.shareholderId}`,
                                                                        )}
                                                                    >
                                                                        <Table.Cell>
                                                                            <span className="flex flex-col">
                                                                                <span className="text-foreground">
                                                                                    {
                                                                                        row.name
                                                                                    }
                                                                                </span>
                                                                                {row.isManual ? (
                                                                                    <span className="text-xs text-warning-soft-foreground">
                                                                                        {t(
                                                                                            'dividends.declare.manualAllocation',
                                                                                        )}
                                                                                    </span>
                                                                                ) : null}
                                                                                {row.citExempt ? (
                                                                                    <span className="text-xs text-muted">
                                                                                        {t(
                                                                                            'dividends.citExempt',
                                                                                        )}
                                                                                    </span>
                                                                                ) : null}
                                                                            </span>
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            <span className="block whitespace-nowrap text-right tabular-nums text-muted">
                                                                                {
                                                                                    row.holdingText
                                                                                }
                                                                            </span>
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            <TextField
                                                                                aria-label={t(
                                                                                    'dividends.declare.overrideAria',
                                                                                    {
                                                                                        name: row.name,
                                                                                    },
                                                                                )}
                                                                                className="ml-auto flex w-28 flex-col"
                                                                                onChange={(
                                                                                    value,
                                                                                ) =>
                                                                                    model.onOverrideChange(
                                                                                        row.shareholderId,
                                                                                        value,
                                                                                    )
                                                                                }
                                                                                value={
                                                                                    model
                                                                                        .overrides[
                                                                                        row
                                                                                            .shareholderId
                                                                                    ] ??
                                                                                    ''
                                                                                }
                                                                                {...getNodeTestIdProp(
                                                                                    `dividends-declare-override-${row.shareholderId}`,
                                                                                )}
                                                                            >
                                                                                <Input
                                                                                    className="text-right tabular-nums"
                                                                                    inputMode="decimal"
                                                                                    placeholder={
                                                                                        row.grossText
                                                                                    }
                                                                                    variant="secondary"
                                                                                />
                                                                            </TextField>
                                                                        </Table.Cell>
                                                                        {[
                                                                            row.withholdingText,
                                                                            row.netText,
                                                                            row.citText,
                                                                        ].map(
                                                                            (
                                                                                value,
                                                                                column,
                                                                            ) => (
                                                                                <Table.Cell
                                                                                    key={
                                                                                        column
                                                                                    }
                                                                                >
                                                                                    <span
                                                                                        className={cn(
                                                                                            'block whitespace-nowrap text-right tabular-nums',
                                                                                            row.isZero
                                                                                                ? 'text-muted'
                                                                                                : 'text-foreground',
                                                                                        )}
                                                                                    >
                                                                                        {
                                                                                            value
                                                                                        }
                                                                                    </span>
                                                                                </Table.Cell>
                                                                            ),
                                                                        )}
                                                                    </Table.Row>
                                                                ),
                                                            )}
                                                            {/* The total is the last
                                                                body row, not a
                                                                `Table.Footer`: the
                                                                footer slot renders
                                                                outside the table, and
                                                                the totals must stay
                                                                column-aligned with the
                                                                figures above them. */}
                                                            <Table.Row
                                                                className="border-t-2 border-foreground/50 bg-surface-secondary"
                                                                id="dividends-declare-total-row"
                                                                key="dividends-declare-total-row"
                                                            >
                                                                <Table.Cell>
                                                                    <span className="text-sm font-semibold">
                                                                        {t(
                                                                            'dividends.declare.total',
                                                                        )}
                                                                    </span>
                                                                </Table.Cell>
                                                                <Table.Cell>
                                                                    {''}
                                                                </Table.Cell>
                                                                {[
                                                                    model
                                                                        .figures
                                                                        .grossDividendText,
                                                                    model
                                                                        .figures
                                                                        .withholdingText,
                                                                    model
                                                                        .figures
                                                                        .netText,
                                                                    model
                                                                        .figures
                                                                        .citText,
                                                                ].map(
                                                                    (
                                                                        value,
                                                                        column,
                                                                    ) => (
                                                                        <Table.Cell
                                                                            key={
                                                                                column
                                                                            }
                                                                            {...getNodeTestIdProp(
                                                                                `dividends-declare-total-${column}`,
                                                                            )}
                                                                        >
                                                                            <span className="block whitespace-nowrap text-right text-sm font-bold tabular-nums text-foreground">
                                                                                {
                                                                                    value
                                                                                }
                                                                            </span>
                                                                        </Table.Cell>
                                                                    ),
                                                                )}
                                                            </Table.Row>
                                                        </Table.Body>
                                                    </Table.Content>
                                                </Table.ScrollContainer>
                                            </Table>
                                        </div>

                                        {model.hasOverrides ? (
                                            <div className="flex items-center justify-between gap-2 text-xs text-muted">
                                                <span>
                                                    {t(
                                                        'dividends.declare.overrideNote',
                                                    )}
                                                </span>
                                                <Button
                                                    onPress={
                                                        model.onClearOverrides
                                                    }
                                                    size="sm"
                                                    variant="ghost"
                                                    {...getNodeTestIdProp(
                                                        'dividends-declare-clear-overrides',
                                                    )}
                                                >
                                                    {t(
                                                        'dividends.declare.clearOverrides',
                                                    )}
                                                </Button>
                                            </div>
                                        ) : null}

                                        <div
                                            className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-surface-secondary px-3 py-2"
                                            {...getNodeTestIdProp(
                                                'dividends-declare-charge',
                                            )}
                                        >
                                            <span className="text-xs text-muted">
                                                {t(
                                                    'dividends.declare.chargeNote',
                                                )}
                                            </span>
                                            <span className="flex items-baseline gap-2 text-sm">
                                                <span className="text-muted">
                                                    {t(
                                                        'dividends.declare.chargeLabel',
                                                    )}
                                                </span>
                                                <span className="font-bold tabular-nums text-foreground">
                                                    {model.figures.chargeText}
                                                </span>
                                            </span>
                                        </div>

                                        {model.declaration ? (
                                            <p className="flex items-center gap-2 text-xs text-muted">
                                                <Icon
                                                    aria-hidden
                                                    icon="gravity-ui:calendar"
                                                />
                                                {t(
                                                    'dividends.declare.dueDate',
                                                    {
                                                        date: isoToDisplayDate(
                                                            model.declaration
                                                                .dueDate,
                                                        ),
                                                    },
                                                )}
                                            </p>
                                        ) : null}

                                        {model.blockedReason ===
                                        'EXCEEDS_DISTRIBUTABLE' ? (
                                            <p
                                                className="flex items-start gap-2 rounded-xl bg-danger-soft px-3 py-2 text-sm text-danger-soft-foreground"
                                                {...getNodeTestIdProp(
                                                    'dividends-declare-blocked',
                                                )}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    className="mt-0.5 shrink-0"
                                                    icon="gravity-ui:circle-xmark"
                                                />
                                                {t(
                                                    'dividends.declare.exceedsDistributable',
                                                    {
                                                        shortfall: formatGel(
                                                            model.quote
                                                                ?.shortfallTetri ??
                                                                0,
                                                        ),
                                                    },
                                                )}
                                            </p>
                                        ) : null}

                                        {model.blockedReason === 'NIL' ? (
                                            <p
                                                className="flex items-center gap-2 text-sm text-muted"
                                                {...getNodeTestIdProp(
                                                    'dividends-declare-nil',
                                                )}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    icon="gravity-ui:circle-info"
                                                />
                                                {t('dividends.declare.nothing')}
                                            </p>
                                        ) : null}
                                    </>
                                ) : (
                                    <p className="text-sm text-muted">
                                        {NIL_AMOUNT}{' '}
                                        {t('dividends.declare.previewFirst')}
                                    </p>
                                )}
                            </Modal.Body>

                            <Modal.Footer>
                                <Button
                                    onPress={onClose}
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        'dividends-declare-cancel',
                                    )}
                                >
                                    {t('dividends.actions.cancel')}
                                </Button>
                                <Button
                                    isDisabled={
                                        !model.hasPreview ||
                                        model.blockedReason !== null
                                    }
                                    isPending={model.isCommitting}
                                    onPress={model.onReview}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'dividends-declare-review',
                                    )}
                                >
                                    {t('dividends.actions.reviewEntry')}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>

            <JournalEntryModal
                entry={model.draft}
                isOpen={model.draft !== null}
                onClose={model.onDraftClose}
                onSaved={model.onDraftSaved}
            />
        </>
    );
};
